import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import "./nodatafound.scss";

/**
 * NoDataFound Component
 *
 * A reusable empty state component for when no data is available.
 * Displays an icon, message, optional description, and optional action button.
 *
 * Usage:
 *   <NoDataFound
 *     message="No care plan data available"
 *     description="Care plan will appear once generated."
 *     actionLabel="Refresh"
 *     onAction={() => reload()}
 *     icon="care"
 *   />
 */
const NoDataFound = ({
  message = "No data available",
  description,
  actionLabel,
  onAction,
  icon = "default",
  className = "",
}) => {
  const renderIcon = () => {
    switch (icon) {
      case "assessment":
      case "risk":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="no-data-icon-svg"
          >
            <circle
              cx="24"
              cy="24"
              r="23"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M24 14C23.45 14 23 14.45 23 15V25C23 25.55 23.45 26 24 26C24.55 26 25 25.55 25 25V15C25 14.45 24.55 14 24 14ZM24 28C23.45 28 23 28.45 23 29V30C23 30.55 23.45 31 24 31C24.55 31 25 30.55 25 30V29C25 28.45 24.55 28 24 28Z"
              fill="currentColor"
              opacity="0.4"
            />
          </svg>
        );
      case "care":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="no-data-icon-svg"
          >
            <circle
              cx="24"
              cy="24"
              r="23"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M32 16H27.82C27.4 14.84 26.3 14 25 14C23.7 14 22.6 14.84 22.18 16H18C16.9 16 16 16.9 16 18V32C16 33.1 16.9 34 18 34H32C33.1 34 34 33.1 34 32V18C34 16.9 33.1 16 32 16ZM25 16C25.55 16 26 16.45 26 17C26 17.55 25.55 18 25 18C24.45 18 24 17.55 24 17C24 16.45 24.45 16 25 16ZM28 30H20V28H28V30ZM30 26H20V24H30V26ZM30 22H20V20H30V22Z"
              fill="currentColor"
              opacity="0.4"
            />
          </svg>
        );
      case "activity":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="no-data-icon-svg"
          >
            <circle
              cx="24"
              cy="24"
              r="23"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M18 27.2H21V35H18V27.2ZM22.6 21H25.4V35H22.6V21ZM27.2 29H30V35H27.2V29Z"
              fill="currentColor"
              opacity="0.4"
            />
          </svg>
        );
      case "mobility":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="no-data-icon-svg"
          >
            <circle
              cx="24"
              cy="24"
              r="23"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M25.5 17.5C26.6 17.5 27.5 16.6 27.5 15.5C27.5 14.4 26.6 13.5 25.5 13.5C24.4 13.5 23.5 14.4 23.5 15.5C23.5 16.6 24.4 17.5 25.5 17.5ZM21.8 20.9L19 35H21.1L22.9 27L25 29V35H27V27.5L24.9 25.5L25.5 22.5C26.8 24 28.8 25 31 25V23C29.1 23 27.5 22 26.7 20.6L25.7 19C25.3 18.4 24.7 18 24 18C23.7 18 23.5 18.1 23.2 18.1L18 20.3V25H20V21.6L21.8 20.9Z"
              fill="currentColor"
              opacity="0.4"
            />
          </svg>
        );
      default:
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="no-data-icon-svg"
          >
            <circle
              cx="24"
              cy="24"
              r="23"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              opacity="0.3"
            />
            <path
              d="M17 17H31V19H17V17ZM17 23H31V25H17V23ZM17 29H26V31H17V29Z"
              fill="currentColor"
              opacity="0.4"
            />
          </svg>
        );
    }
  };

  return (
    <div className={`no-data-found ${className}`}>
      <div className="no-data-icon">{renderIcon()}</div>
      <Typography className="no-data-message">{message}</Typography>
      {description && (
        <Typography className="no-data-description">{description}</Typography>
      )}
      {actionLabel && onAction && (
        <button className="btn btn-outline no-data-action" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};

NoDataFound.propTypes = {
  message: PropTypes.string,
  description: PropTypes.string,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  icon: PropTypes.oneOf([
    "default",
    "assessment",
    "risk",
    "care",
    "activity",
    "mobility",
  ]),
  className: PropTypes.string,
};

export default NoDataFound;
